import { getJson } from "./client";
import type { SimulationResult } from "./types";

export interface PreviewPosition {
	assetId: string | null;
	protocol: string;
	vaultAddress: string | null;
	balance: string;
	pricePerUnit: string;
	valueUsdc: string;
	lastUpdate: string;
}

export interface PreviewQuoteSummary {
	assetId: string;
	expectedNetApy: string;
	pricePerUnitUsdc: string;
	exitTimeDays: number;
	maturity: string | null;
	costBpsIn: number;
	costBpsOut: number;
	observedAt: string;
}

export interface RebalanceDecision {
	shouldRebalance: boolean;
	expectedApyCurrent: string;
	expectedApyTarget: string;
	expectedApyDelta: string;
	totalValueRaw: string;
	expectedBenefitRaw: string;
	estimatedTradingCostRaw: string;
	expectedNetBenefitRaw: string;
	reason: string;
}

export interface RebalanceState {
	cashWeight: string;
	assetWeights: Record<string, string>;
}

export interface RebalanceTradeWeights {
	assetBuyWeights: Record<string, string>;
	assetSellWeights: Record<string, string>;
	cashBuyWeight: string;
	cashSellWeight: string;
}

export interface RebalanceDeltaValues {
	assetDeltaValuesRaw: Record<string, string>;
	cashDeltaValueRaw: string;
}

export interface RebalanceCostEstimate {
	expectedExecutionCostRaw: string;
	expectedGasCostRaw: string;
	expectedTotalCostRaw: string;
}

export interface OptimizerDiagnostics {
	shadowPrices: Record<string, number>;
	binding: string[];
	objectiveValue: number;
	status: string;
}

export interface RebalancePreviewResponse {
	wallet: string;
	asOf: string;
	input: {
		cashUsdcRaw: string;
		positions: PreviewPosition[];
		quotes: PreviewQuoteSummary[];
	};
	decision: RebalanceDecision;
	valuation: {
		totalValueRaw: string;
		cashValueRaw: string;
		cashWeight: string;
	};
	stateOld: RebalanceState;
	stateNew: RebalanceState;
	tradeWeights: RebalanceTradeWeights;
	deltaValues: RebalanceDeltaValues;
	expectedApys: Record<string, string>;
	costEstimate: RebalanceCostEstimate;
	diagnostics: OptimizerDiagnostics;
}

export interface PreviewQuery {
	/** Omit to use the worker's `KAM_WALLET_ADDRESS` default. */
	wallet?: string;
	cashUsdc?: string;
	mode?: "deposit" | "withdrawal";
	cashBufferWeight?: string;
	requiredCashWeight?: string;
}

export interface RebalanceSimulationResponse extends RebalancePreviewResponse {
	simulation: SimulationResult;
}

export function fetchRebalancePreview(
	q: PreviewQuery = {},
): Promise<RebalancePreviewResponse> {
	return getJson<RebalancePreviewResponse>(
		buildPreviewUrl("/rebalance/preview", q),
	);
}

export function fetchRebalanceSimulation(
	q: PreviewQuery = {},
): Promise<RebalanceSimulationResponse> {
	return getJson<RebalanceSimulationResponse>(
		buildPreviewUrl("/rebalance/simulate", q),
	);
}

function buildPreviewUrl(base: string, q: PreviewQuery): string {
	const sp = new URLSearchParams();
	if (q.wallet) sp.set("wallet", q.wallet);
	if (q.cashUsdc) sp.set("cashUsdc", q.cashUsdc);
	if (q.mode) sp.set("mode", q.mode);
	if (q.cashBufferWeight) sp.set("cashBufferWeight", q.cashBufferWeight);
	if (q.requiredCashWeight) sp.set("requiredCashWeight", q.requiredCashWeight);
	const qs = sp.toString();
	return qs ? `${base}?${qs}` : base;
}
